/**
 * User Management Tab - טאב ניהול משתמשים
 * List, filter, and manage users
 */

import { useEffect, useState } from 'react'
import { useAdminStore } from '../../store/adminStore'
import { useAuthStore } from '../../store/authStore'
import { Search, Filter, RefreshCw, Eye, Lock, Unlock, MessageSquare, Trash2, Cpu, Sparkles, DollarSign, KeyRound, AlertTriangle, ShieldAlert, CheckCircle, Brain, GraduationCap } from 'lucide-react'
import type { AdminUser } from '../../types'
import { roleLabel } from '../../config/roles'
import UserDetailModal from './UserDetailModal'
import UserStatusModal from './UserStatusModal'
import UserConversationsModal from './UserConversationsModal'
import UserDeleteModal from './UserDeleteModal'
import UserCostModal from './UserCostModal'
import AdminMemoryModal from './AdminMemoryModal'

export default function UserManagementTab() {
  const {
    users,
    usersTotal,
    usersLoading,
    usersError,
    userFilters,
    usersPagination,
    fetchUsers,
    setUserFilters,
    setUsersPagination,
    clearUsersError,
    userCosts,
    fetchUsersCosts,
    sendPasswordReset,
    unlockUser,
    verifyUserEmail,
    resetStudentAllowance
  } = useAdminStore()

  const currentUser = useAuthStore((state) => state.user)
  const [showUserDetail, setShowUserDetail] = useState(false)
  const [showStatusModal, setShowStatusModal] = useState(false)
  const [showConversations, setShowConversations] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [showCostModal, setShowCostModal] = useState(false)
  const [showMemoryModal, setShowMemoryModal] = useState(false)
  const [selectedUser, setSelectedUser] = useState<AdminUser | null>(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [showFilters, setShowFilters] = useState(false)
  const [actionMessage, setActionMessage] = useState<string | null>(null)

  // Default to the "app users" view (everyone except students) on first mount,
  // unless the admin already switched to the students view.
  useEffect(() => {
    if (userFilters.role !== 'student' && userFilters.excludeStudents === undefined) {
      setUserFilters({ excludeStudents: true })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Fetch users and costs on mount and when filters/pagination change
  useEffect(() => {
    fetchUsers()
    fetchUsersCosts()
  }, [userFilters, usersPagination.page])

  // Handle search with debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchTerm !== userFilters.search) {
        setUserFilters({ search: searchTerm || undefined })
        setUsersPagination({ page: 1 })
      }
    }, 500)

    return () => clearTimeout(timer)
  }, [searchTerm])

  const handleViewUser = (user: AdminUser) => {
    setSelectedUser(user)
    setShowUserDetail(true)
  }

  const handleChangeStatus = (user: AdminUser) => {
    setSelectedUser(user)
    setShowStatusModal(true)
  }

  const handleViewConversations = (user: AdminUser) => {
    setSelectedUser(user)
    setShowConversations(true)
  }

  const handleViewCost = (user: AdminUser) => {
    setSelectedUser(user)
    setShowCostModal(true)
  }

  const handleEditMemory = (user: AdminUser) => {
    setSelectedUser(user)
    setShowMemoryModal(true)
  }

  const formatCost = (cost: number) => {
    if (cost === 0) return '—'
    if (cost < 0.01) return `$${cost.toFixed(4)}`
    return `$${cost.toFixed(2)}`
  }

  const handleDeleteUser = (user: AdminUser) => {
    setSelectedUser(user)
    setShowDeleteModal(true)
  }

  const handleSendReset = async (user: AdminUser) => {
    if (!window.confirm(`לשלוח קישור לאיפוס סיסמה אל ${user.email}?`)) return
    setActionMessage(null)
    try {
      await sendPasswordReset(user._id)
      setActionMessage(`נשלח קישור לאיפוס סיסמה אל ${user.email}`)
    } catch {
      // error surfaced via usersError
    }
  }

  const handleUnlock = async (user: AdminUser) => {
    if (!window.confirm(`לשחרר את חשבון ${user.email}? פעולה זו מאפסת את ניסיונות ההתחברות הכושלים.`)) return
    setActionMessage(null)
    try {
      await unlockUser(user._id)
      setActionMessage(`החשבון של ${user.email} שוחרר — ניתן כעת להתחבר`)
    } catch {
      // error surfaced via usersError
    }
  }

  const handleVerifyEmail = async (user: AdminUser) => {
    if (!window.confirm(`לאשר ידנית את הדוא״ל של ${user.email}? המשתמש יוכל להתחבר ללא קישור אימות.`)) return
    setActionMessage(null)
    try {
      await verifyUserEmail(user._id)
      setActionMessage(`הדוא״ל של ${user.email} אומת — המשתמש יכול כעת להתחבר`)
    } catch {
      // error surfaced via usersError
    }
  }

  const handleResetAllowance = async (user: AdminUser) => {
    if (!window.confirm(`לאפס את מכסת הלימוד (AI) של ${user.email}?`)) return
    setActionMessage(null)
    try {
      await resetStudentAllowance(user._id)
      setActionMessage(`מכסת הלימוד של ${user.email} אופסה`)
    } catch {
      // error surfaced via usersError
    }
  }

  // True when a user is currently locked out by failed-login protection
  const isLocked = (user: AdminUser) =>
    !!user.accountLockedUntil && new Date(user.accountLockedUntil).getTime() > Date.now()

  // Privileged accounts are never deleted from here — the rights have to be
  // removed first, which is a separate, audited decision.
  const canDeleteUser = (user: AdminUser) =>
    currentUser?.role === 'admin' &&
    user.role !== 'admin' &&
    user.role !== 'advisor' &&
    user.role !== 'sub_admin' &&
    user._id !== currentUser._id

  const handlePageChange = (newPage: number) => {
    setUsersPagination({ page: newPage })
  }

  const getRiskScoreBadge = (score?: number) => {
    if (!score) return <span className="badge badge-gray">לא הושלם</span>

    if (score >= 8) return <span className="badge badge-error">{score.toFixed(1)}</span>
    if (score >= 6) return <span className="badge badge-warning">{score.toFixed(1)}</span>
    if (score >= 4) return <span className="badge badge-info">{score.toFixed(1)}</span>
    return <span className="badge badge-success">{score.toFixed(1)}</span>
  }

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return <span className="badge badge-success">פעיל</span>
      case 'suspended':
        return <span className="badge badge-error">מושעה</span>
      case 'pending_verification':
        return <span className="badge badge-warning">ממתין לאימות</span>
      case 'deleted':
        return <span className="badge badge-gray">נמחק</span>
      default:
        return <span className="badge">{status}</span>
    }
  }

  // Badges that explain why a user might be unable to log in
  const getLoginHealthBadges = (user: AdminUser) => {
    const badges: JSX.Element[] = []

    if (isLocked(user)) {
      const until = new Date(user.accountLockedUntil as string).toLocaleString('he-IL')
      badges.push(
        <span key="locked" className="badge badge-error inline-flex items-center gap-1" title={`נעול עד ${until}`}>
          <Lock size={12} aria-hidden="true" /> נעול
        </span>
      )
    }

    if (!user.emailVerified) {
      badges.push(
        <span key="unverified" className="badge badge-warning inline-flex items-center gap-1" title="הדוא״ל לא אומת">
          <AlertTriangle size={12} aria-hidden="true" /> דוא״ל לא מאומת
        </span>
      )
    }

    if (!user.lastLoginAt) {
      badges.push(
        <span key="never" className="badge badge-gray" title="המשתמש מעולם לא התחבר">
          לא התחבר מעולם
        </span>
      )
    }

    if (!isLocked(user) && user.failedLoginAttempts > 0) {
      badges.push(
        <span key="failed" className="badge badge-warning inline-flex items-center gap-1" title="ניסיונות התחברות כושלים">
          <ShieldAlert size={12} aria-hidden="true" /> {user.failedLoginAttempts} כשלים
        </span>
      )
    }

    if (badges.length === 0) {
      return <span className="text-gray-300">—</span>
    }

    return <div className="flex flex-wrap gap-1">{badges}</div>
  }

  const getModelShortName = (id: string): string => {
    if (id.includes('opus-4-6')) return 'Opus 4.6'
    if (id.includes('sonnet-4-6')) return 'Sonnet 4.6'
    if (id.includes('opus-4-')) return 'Opus 4'
    if (id.includes('sonnet-4-5')) return 'Sonnet 4.5'
    if (id.includes('sonnet-4-')) return 'Sonnet 4'
    if (id.includes('haiku-4-5')) return 'Haiku 4.5'
    if (id.includes('3-5-sonnet')) return 'Sonnet 3.5'
    if (id.includes('3-5-haiku')) return 'Haiku 3.5'
    if (id.includes('3-opus')) return 'Opus 3'
    if (id.includes('3-sonnet')) return 'Sonnet 3'
    if (id.includes('3-haiku')) return 'Haiku 3'
    return id.replace('claude-', '')
  }

  // Which table the admin is viewing: app users (everyone except students) vs students.
  const view: 'app' | 'students' = userFilters.role === 'student' ? 'students' : 'app'
  const applyView = (v: 'app' | 'students') => {
    if (v === 'students') {
      setUserFilters({ role: 'student', excludeStudents: undefined })
    } else {
      setUserFilters({ role: undefined, excludeStudents: true })
    }
    setUsersPagination({ page: 1 })
  }

  return (
    <div className="space-y-6">
      {/* App users vs Students — two separate tables */}
      <div
        className="inline-flex rounded-lg border border-gray-200 bg-gray-50 p-1"
        role="tablist"
        aria-label="סוג משתמשים"
      >
        <button
          role="tab"
          aria-selected={view === 'app'}
          onClick={() => applyView('app')}
          className={`flex items-center gap-2 px-4 py-1.5 rounded-md text-sm font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 ${
            view === 'app' ? 'bg-white text-primary-700 shadow-sm' : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          משתמשי אפליקציה
        </button>
        <button
          role="tab"
          aria-selected={view === 'students'}
          onClick={() => applyView('students')}
          className={`flex items-center gap-2 px-4 py-1.5 rounded-md text-sm font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 ${
            view === 'students' ? 'bg-white text-primary-700 shadow-sm' : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          <GraduationCap size={16} aria-hidden="true" />
          תלמידים
        </button>
      </div>

      {/* Header with Search and Actions */}
      <div className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
        {/* Search */}
        <div className="relative flex-1 max-w-md">
          <Search className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} aria-hidden="true" />
          <input
            type="text"
            placeholder="חיפוש לפי שם או דוא״ל..."
            aria-label="חיפוש לפי שם או דוא״ל"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="input pr-10 w-full"
          />
        </div>

        {/* Actions */}
        <div className="flex gap-2">
          <button
            onClick={() => {
              const next = !userFilters.loginBlocked
              setUserFilters({ loginBlocked: next || undefined })
              setUsersPagination({ page: 1 })
            }}
            className={`btn ${userFilters.loginBlocked ? 'btn-primary' : 'btn-outline'}`}
            title="הצג רק משתמשים שלא מצליחים להתחבר (נעולים / לא מאומתים / מושעים)"
          >
            <ShieldAlert size={18} aria-hidden="true" />
            בעיות התחברות
          </button>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="btn btn-outline"
          >
            <Filter size={18} aria-hidden="true" />
            סינון
          </button>
          <button
            onClick={() => fetchUsers()}
            disabled={usersLoading}
            className="btn btn-outline"
          >
            <RefreshCw size={18} className={usersLoading ? 'animate-spin' : ''} aria-hidden="true" />
            רענון
          </button>
        </div>
      </div>

      {/* Filters Panel */}
      {showFilters && (
        <div className="card bg-gray-50">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Status Filter */}
            <div>
              <label htmlFor="user-filter-status" className="block text-sm font-medium text-gray-700 mb-1">
                סטטוס
              </label>
              <select
                id="user-filter-status"
                value={userFilters.status || ''}
                onChange={(e) =>
                  setUserFilters({ status: e.target.value as any || undefined })
                }
                className="input"
              >
                <option value="">הכל</option>
                <option value="active">פעיל</option>
                <option value="suspended">מושעה</option>
                <option value="pending_verification">ממתין לאימות</option>
              </select>
            </div>

            {/* Role Filter — only in the app-users view (students are role-fixed) */}
            {view === 'app' && (
              <div>
                <label htmlFor="user-filter-role" className="block text-sm font-medium text-gray-700 mb-1">
                  תפקיד
                </label>
                <select
                  id="user-filter-role"
                  value={userFilters.role || ''}
                  onChange={(e) => {
                    const val = e.target.value
                    // Keep excluding students in the app view; a specific role already excludes them.
                    setUserFilters(
                      val
                        ? { role: val as any, excludeStudents: undefined }
                        : { role: undefined, excludeStudents: true }
                    )
                  }}
                  className="input"
                >
                  <option value="">הכל</option>
                  <option value="client">לקוח</option>
                  <option value="advisor">יועץ</option>
                  <option value="admin">מנהל</option>
                </select>
              </div>
            )}

            {/* Questionnaire Filter */}
            <div>
              <label htmlFor="user-filter-questionnaire" className="block text-sm font-medium text-gray-700 mb-1">
                שאלון סיכון
              </label>
              <select
                id="user-filter-questionnaire"
                value={
                  userFilters.hasQuestionnaire === undefined
                    ? ''
                    : userFilters.hasQuestionnaire
                    ? 'yes'
                    : 'no'
                }
                onChange={(e) =>
                  setUserFilters({
                    hasQuestionnaire:
                      e.target.value === ''
                        ? undefined
                        : e.target.value === 'yes'
                  })
                }
                className="input"
              >
                <option value="">הכל</option>
                <option value="yes">הושלם</option>
                <option value="no">לא הושלם</option>
              </select>
            </div>

            {/* Risk Score Range */}
            <div>
              <span className="block text-sm font-medium text-gray-700 mb-1">
                טווח ציון סיכון
              </span>
              <div className="flex gap-2">
                <input
                  type="number"
                  dir="ltr"
                  aria-label="ציון סיכון מינימלי"
                  min="1"
                  max="10"
                  placeholder="מינ"
                  value={userFilters.minRiskScore || ''}
                  onChange={(e) =>
                    setUserFilters({
                      minRiskScore: e.target.value ? Number(e.target.value) : undefined
                    })
                  }
                  className="input text-sm"
                />
                <input
                  type="number"
                  dir="ltr"
                  aria-label="ציון סיכון מקסימלי"
                  min="1"
                  max="10"
                  placeholder="מקס"
                  value={userFilters.maxRiskScore || ''}
                  onChange={(e) =>
                    setUserFilters({
                      maxRiskScore: e.target.value ? Number(e.target.value) : undefined
                    })
                  }
                  className="input text-sm"
                />
              </div>
            </div>
          </div>

          {/* Clear Filters */}
          <div className="mt-4">
            <button
              onClick={() => {
                setUserFilters({})
                setSearchTerm('')
              }}
              className="btn btn-sm btn-outline"
            >
              נקה סינון
            </button>
          </div>
        </div>
      )}

      {/* Error Alert */}
      {usersError && (
        <div className="alert alert-error" role="alert">
          <p>{usersError}</p>
          <button onClick={clearUsersError} className="btn btn-sm btn-outline">
            סגור
          </button>
        </div>
      )}

      {/* Success Alert (reset link sent / account unlocked) */}
      {actionMessage && (
        <div className="alert alert-success" role="status">
          <p>{actionMessage}</p>
          <button onClick={() => setActionMessage(null)} className="btn btn-sm btn-outline">
            סגור
          </button>
        </div>
      )}

      {/* Users Table */}
      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-right text-sm font-medium text-gray-700">
                  שם
                </th>
                <th className="px-4 py-3 text-right text-sm font-medium text-gray-700">
                  דוא״ל
                </th>
                <th className="px-4 py-3 text-right text-sm font-medium text-gray-700">
                  תפקיד
                </th>
                <th className="px-4 py-3 text-right text-sm font-medium text-gray-700">
                  סטטוס
                </th>
                <th className="px-4 py-3 text-right text-sm font-medium text-gray-700">
                  התחברות
                </th>
                <th className="px-4 py-3 text-right text-sm font-medium text-gray-700">
                  ציון סיכון
                </th>
                <th className="px-4 py-3 text-right text-sm font-medium text-gray-700">
                  מודל AI
                </th>
                <th className="px-4 py-3 text-right text-sm font-medium text-gray-700">
                  עלות AI
                </th>
                <th className="px-4 py-3 text-right text-sm font-medium text-gray-700">
                  תאריך רישום
                </th>
                <th className="px-4 py-3 text-right text-sm font-medium text-gray-700">
                  פעולות
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {usersLoading ? (
                <tr>
                  <td colSpan={10} className="px-4 py-8 text-center text-gray-500">
                    טוען משתמשים...
                  </td>
                </tr>
              ) : users.length === 0 ? (
                <tr>
                  <td colSpan={10} className="px-4 py-8 text-center text-gray-500">
                    לא נמצאו משתמשים
                  </td>
                </tr>
              ) : (
                users.map((user) => (
                  <tr key={user._id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm text-gray-900">
                      {user.firstName} {user.lastName}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600 font-mono text-xs">
                      {user.email}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <span className="badge badge-gray">
                        {roleLabel(user.role)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm">{getStatusBadge(user.status)}</td>
                    <td className="px-4 py-3 text-sm">{getLoginHealthBadges(user)}</td>
                    <td className="px-4 py-3 text-sm">
                      {getRiskScoreBadge(user.currentRiskScore)}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {user.assignedModel ? (
                        <span
                          className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-purple-50 text-purple-700 border border-purple-200 rounded-full text-xs font-medium"
                          title={`מודל ייעודי: ${user.assignedModel}`}
                        >
                          <Sparkles size={12} className="text-purple-500" aria-hidden="true" />
                          {getModelShortName(user.assignedModel)}
                        </span>
                      ) : (
                        <span
                          className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-gray-50 text-gray-500 border border-gray-200 rounded-full text-xs"
                          title="משתמש במודל ברירת המחדל של המערכת"
                        >
                          <Cpu size={12} className="text-gray-400" aria-hidden="true" />
                          ברירת מחדל
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {(() => {
                        const cost = userCosts[user._id]
                        if (!cost || cost.totalCost === 0) {
                          return <span className="text-gray-300">—</span>
                        }
                        return (
                          <button
                            onClick={() => handleViewCost(user)}
                            className="inline-flex items-center gap-1 px-2 py-1 bg-green-50 text-green-700 border border-green-200 rounded-full text-xs font-medium hover:bg-green-100 transition-colors cursor-pointer"
                            title={`${cost.totalCalls} קריאות | ${cost.totalTokens.toLocaleString()} טוקנים — לחץ לפירוט`}
                          >
                            <DollarSign size={12} aria-hidden="true" />
                            {formatCost(cost.totalCost)}
                          </button>
                        )
                      })()}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {new Date(user.createdAt).toLocaleDateString('he-IL')}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleViewUser(user)}
                          className="text-primary-600 hover:text-primary-800"
                          title="צפייה בפרטים"
                          aria-label="צפייה בפרטים"
                        >
                          <Eye size={18} aria-hidden="true" />
                        </button>
                        <button
                          onClick={() => handleViewConversations(user)}
                          className="text-green-600 hover:text-green-800"
                          title="שיחות"
                          aria-label="שיחות"
                        >
                          <MessageSquare size={18} aria-hidden="true" />
                        </button>
                        <button
                          onClick={() => handleEditMemory(user)}
                          className="text-purple-600 hover:text-purple-800"
                          title="זיכרון AI"
                          aria-label="זיכרון AI"
                        >
                          <Brain size={18} aria-hidden="true" />
                        </button>
                        {!user.emailVerified && (
                          <button
                            onClick={() => handleVerifyEmail(user)}
                            disabled={usersLoading}
                            className="text-emerald-600 hover:text-emerald-800 disabled:opacity-40"
                            title="אשר דוא״ל ידנית"
                            aria-label="אשר דוא״ל ידנית"
                          >
                            <CheckCircle size={18} aria-hidden="true" />
                          </button>
                        )}
                        <button
                          onClick={() => handleSendReset(user)}
                          disabled={usersLoading}
                          className="text-blue-600 hover:text-blue-800 disabled:opacity-40"
                          title="שלח קישור לאיפוס סיסמה"
                          aria-label="שלח קישור לאיפוס סיסמה"
                        >
                          <KeyRound size={18} aria-hidden="true" />
                        </button>
                        {isLocked(user) && (
                          <button
                            onClick={() => handleUnlock(user)}
                            disabled={usersLoading}
                            className="text-amber-600 hover:text-amber-800 disabled:opacity-40"
                            title="שחרר חשבון נעול"
                            aria-label="שחרר חשבון נעול"
                          >
                            <Unlock size={18} aria-hidden="true" />
                          </button>
                        )}
                        {user.role === 'student' && (
                          <button
                            onClick={() => handleResetAllowance(user)}
                            disabled={usersLoading}
                            className="text-teal-600 hover:text-teal-800 disabled:opacity-40"
                            title="אפס מכסת לימוד (AI)"
                            aria-label="אפס מכסת לימוד"
                          >
                            <GraduationCap size={18} aria-hidden="true" />
                          </button>
                        )}
                        <button
                          onClick={() => handleChangeStatus(user)}
                          className="text-gray-600 hover:text-gray-800"
                          title="שינוי סטטוס"
                          aria-label="שינוי סטטוס"
                        >
                          {user.status === 'active' ? (
                            <Lock size={18} aria-hidden="true" />
                          ) : (
                            <Unlock size={18} aria-hidden="true" />
                          )}
                        </button>
                        {canDeleteUser(user) && (
                          <button
                            onClick={() => handleDeleteUser(user)}
                            className="text-red-600 hover:text-red-800"
                            title="מחיקת משתמש"
                            aria-label="מחיקת משתמש"
                          >
                            <Trash2 size={18} aria-hidden="true" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {usersTotal > usersPagination.limit && (
          <div className="border-t border-gray-200 px-4 py-3 flex items-center justify-between">
            <p className="text-sm text-gray-700">
              מציג {users.length} מתוך {usersTotal} משתמשים
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => handlePageChange(usersPagination.page - 1)}
                disabled={usersPagination.page === 1}
                className="btn btn-sm btn-outline"
              >
                הקודם
              </button>
              <span className="px-3 py-1 text-sm">
                עמוד {usersPagination.page}
              </span>
              <button
                onClick={() => handlePageChange(usersPagination.page + 1)}
                disabled={users.length < usersPagination.limit}
                className="btn btn-sm btn-outline"
              >
                הבא
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Modals */}
      {showUserDetail && selectedUser && (
        <UserDetailModal
          userId={selectedUser._id}
          onClose={() => {
            setShowUserDetail(false)
            setSelectedUser(null)
          }}
        />
      )}

      {showStatusModal && selectedUser && (
        <UserStatusModal
          user={selectedUser}
          onClose={() => {
            setShowStatusModal(false)
            setSelectedUser(null)
          }}
          onSuccess={() => {
            setShowStatusModal(false)
            setSelectedUser(null)
            fetchUsers()
          }}
        />
      )}

      {showConversations && selectedUser && (
        <UserConversationsModal
          userId={selectedUser._id}
          userName={`${selectedUser.firstName} ${selectedUser.lastName}`}
          onClose={() => {
            setShowConversations(false)
            setSelectedUser(null)
          }}
        />
      )}

      {showDeleteModal && selectedUser && (
        <UserDeleteModal
          user={selectedUser}
          onClose={() => {
            setShowDeleteModal(false)
            setSelectedUser(null)
          }}
          onSuccess={() => {
            setShowDeleteModal(false)
            setSelectedUser(null)
          }}
        />
      )}

      {showCostModal && selectedUser && (
        <UserCostModal
          userId={selectedUser._id}
          userName={`${selectedUser.firstName} ${selectedUser.lastName}`}
          onClose={() => {
            setShowCostModal(false)
            setSelectedUser(null)
          }}
        />
      )}

      {showMemoryModal && selectedUser && (
        <AdminMemoryModal
          userId={selectedUser._id}
          userName={`${selectedUser.firstName} ${selectedUser.lastName}`}
          onClose={() => {
            setShowMemoryModal(false)
            setSelectedUser(null)
          }}
        />
      )}
    </div>
  )
}
